import React, { useEffect } from 'react'
import {
	Field, getFormValues,
	InjectedFormProps,
	reduxForm
} from 'redux-form'
import {
	Button, Col, Form, Row, Select, Tag
} from 'antd'
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { map } from 'lodash'

import CustomOptionsSelectField from '../../../atoms/form/CustomOptionsSelectField'
import validateForm from './validateComplaintForm'
import { FORMS } from '../../../utils/enums'
import { RootState } from '../../../reducers'
import {
	COMPLAINT_STATUS, IComplaintForm, translateComplaintStatus, translateComplaintStatusColor
} from '../../../reducers/complaints/actions'
import TextareaField from '../../../atoms/TextareaField'
import { getJobs } from '../../../reducers/jobs/actions'
import JobFinishesDrawerField from '../../../components/JobFinishesDrawerField'

const { Option } = Select

interface ComponentProps {
	isCreate: boolean
	id?: number
	onRemove?: (id?: number) => void
}

type Props = InjectedFormProps<IComplaintForm, ComponentProps> & ComponentProps

const normalize = (str: string) => str
	.normalize('NFD') // rozdelí znaky a diakritiku
	.replace(/[\u0300-\u036f]/g, '') // odstráni diakritiku
	.toLowerCase()

const ComplaintForm = (props: Props) => {
	const { t } = useTranslation('components')

	const {
		handleSubmit, invalid, pristine, isCreate
	} = props

	const dispatch = useDispatch()

	const complaintForm = (useSelector((state: RootState) => getFormValues(FORMS.COMPLAINT_FORM)(state)) as IComplaintForm)

	useEffect(() => {
		dispatch(getJobs({ limit: 1000, page: 1, queryParams: {} }))
	}, [])

	const jobs = useSelector((state: RootState) => state.jobs.jobs.tableData || [])
		.map((job: any) => ({
			value: job.id,
			label: job.name || job.address
		}))

	return (
		<Form layout={'vertical'} onSubmitCapture={handleSubmit}>
			<Row gutter={16}>
				<Col span={18} className={'grid'}>
					<div className={'flex direction-col justify-start main-content'}>
						<h1>{isCreate ? t('pages:Pridať sťažnosť') : t('pages:Detail sťažnosti')}</h1>
						<Field
							name={'jobID'}
							component={CustomOptionsSelectField}
							label={t('components:Zákazka')}
							size={'large'}
							options={map(jobs, (option) => (
								<Option
									key={option.value}
									value={option.value}
								>
									{option.label}
								</Option>
							))}
							required
							showSearch
							allowClear
							filterOption={(input: any, option: any) => normalize(option.children).includes(normalize(input))
							}
						/>
						<Field
							name="finishID"
							component={JobFinishesDrawerField}
							label={t('components:Dokončenie')}
							jobID={complaintForm?.jobID}
							numberOfMonths={2}
							defaultStringValue={complaintForm?.finishLabel}
							disabled={complaintForm?.jobID === undefined || complaintForm?.jobID === null}
						/>
						<Field
							name={'status'}
							component={CustomOptionsSelectField}
							label={t('components:Stav')}
							size={'large'}
							options={Object.values(COMPLAINT_STATUS).map((status) => (
								<Option key={status} value={status}>
									<Tag color={translateComplaintStatusColor(status)}>
										{translateComplaintStatus(status, t)}
									</Tag>
								</Option>
							))}
							required
							showSearch
							allowClear
							filterOption={(input: any, option: any) => option.children?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0}
						/>
						<Field
							name={'note'}
							required={true}
							component={TextareaField}
							label={t('components:Poznámka')}
							rows={6}
						/>
					</div>
				</Col>
				<Col span={6} className={'grid'}>
					<div className={'flex direction-col justify-between sidebar-content'}>
						<div className={'flex direction-col justify-center'}>
							<Button
								icon={<SaveOutlined />}
								onClick={handleSubmit}
								disabled={pristine || invalid}
								type={'primary'}
								style={{ margin: '0px' }}
							>
								{t('pages:Uložiť')}
							</Button>
						</div>

						{!isCreate && (
							<Button
								icon={<DeleteOutlined />}
								style={{ marginTop: 16 }}
								type={'primary'}
								danger
								onClick={() => props.onRemove?.(props.id)}
							>
								{t('pages:Odstrániť')}
							</Button>
						)}
					</div>
				</Col>

			</Row>
		</Form>
	)
}

export default reduxForm<IComplaintForm, ComponentProps>({
	form: FORMS.COMPLAINT_FORM,
	forceUnregisterOnUnmount: true,
	touchOnChange: true,
	destroyOnUnmount: true,
	validate: validateForm
})(ComplaintForm)
